if (!customElements.get("collections-filtering")) {
  class CollectionsFiltering extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector("form");

      if (!this.form) {
        return;
      }

      this.button = this.querySelector('button[type="submit"]');

      // Category / collection dropdown.
      this.select = this.querySelector("select.dropdown-collection");

      // Used when only one collection has been configured.
      this.input = this.querySelector("input.input-collection");

      // Make, Model, etc.
      this.selects = Array.from(
        this.querySelectorAll("select.dropdown-filter")
      );

      /*
       * Horizontal section:
       *
       * Make | Model | Category | Search
       */
      this.isHorizontalFilter =
        !!this.closest(".section-filters") &&
        !this.closest(".section-vertical-filters");

      /*
       * Other/vertical filter section.
       */
      this.isVerticalFilter =
        !!this.closest(".section-vertical-filters");

      /*
       * Used to cancel an older fetch when another
       * filter request starts.
       */
      this.requestController = null;

      this.setupEventListeners();

      if (this.isHorizontalFilter) {
        this.initHorizontalFilter();
      } else {
        this.initDefaultFilter();
      }
    }

    /* ============================================================
       EVENT LISTENERS
    ============================================================ */

    setupEventListeners() {
      /*
       * SEARCH
       */
      this.form.addEventListener("submit", (event) => {
        event.preventDefault();

        if (!this.form.getAttribute("action")) {
          return;
        }

        if (this.button && this.button.disabled) {
          return;
        }

        window.location.href =
          this.form.getAttribute("action");
      });

      /*
       * ==========================================================
       * HORIZONTAL FILTER
       * ==========================================================
       *
       * IMPORTANT:
       *
       * Every field is independent now.
       *
       * Make does NOT unlock Model.
       * Model does NOT unlock Category.
       *
       * All are available from the beginning.
       */
      if (this.isHorizontalFilter) {
        this.selects.forEach((select) => {
          select.addEventListener(
            "change",
            this.onHorizontalFilterChange.bind(this)
          );
        });

        if (this.select) {
          this.select.addEventListener(
            "change",
            this.onHorizontalCollectionChange.bind(this)
          );
        }

        return;
      }

      /*
       * ==========================================================
       * ORIGINAL / VERTICAL FILTER
       * ==========================================================
       */

      if (this.select) {
        this.select.addEventListener(
          "change",
          this.onCollectionChange.bind(this)
        );
      }

      this.selects.forEach((select) => {
        select.addEventListener(
          "change",
          this.onFilterChange.bind(this)
        );
      });
    }

    /* ============================================================
       URL
    ============================================================ */

    constructURL() {
      let collectionUrl;

      /*
       * ----------------------------------------------------------
       * HORIZONTAL FILTER
       * ----------------------------------------------------------
       *
       * If Category is selected:
       * use Category URL.
       *
       * Otherwise:
       * use configured single collection or /collections/all.
       */
      if (this.isHorizontalFilter) {
        collectionUrl =
          this.select?.value ||
          this.input?.value ||
          window.routes.all_products_url;
      } else {
        /*
         * Original section behavior.
         */
        collectionUrl =
          this.select?.value ||
          this.input?.value ||
          window.routes.all_products_url;
      }

      const url = new URL(
        collectionUrl,
        window.requestShopUrl
      );

      /*
       * Add ALL currently selected product filters.
       *
       * Example:
       *
       * /collections/all
       * ?filter.p.m.custom.make=Audi
       *
       * OR
       *
       * /collections/brakes
       * ?filter.p.m.custom.make=Audi
       * &filter.p.m.custom.model=R8
       */
      this.selects.forEach((select) => {
        if (!select.value) {
          return;
        }

        Array.from(select.selectedOptions).forEach(
          (option) => {
            if (!option.dataset.paramName) {
              return;
            }

            url.searchParams.set(
              option.dataset.paramName,
              option.value
            );
          }
        );
      });

      return url.toString();
    }

    setFormUrl(url, background = false) {
      if (!url) {
        return;
      }

      this.form.setAttribute("action", url);

      /*
       * Keep original vertical behavior.
       *
       * Horizontal button state is controlled by
       * updateHorizontalButtonState().
       */
      if (
        !this.isHorizontalFilter &&
        !background &&
        this.button
      ) {
        this.button.disabled = false;
      }
    }

    /* ============================================================
       GENERIC HELPERS
    ============================================================ */

    selectRemoveOptions(select) {
      if (!select) {
        return;
      }

      const options = select.querySelectorAll(
        "option:not(.default)"
      );

      options.forEach((option) => {
        option.remove();
      });

      const defaultOption =
        select.querySelector("option.default") ||
        select.querySelector("option");

      if (defaultOption) {
        defaultOption.selected = true;
      }

      select.value = "";
    }

    disableSelect(select) {
      if (!select) {
        return;
      }

      select.disabled = true;

      const group =
        select.closest(".form-group");

      if (group) {
        group.classList.add("disabled");
      }
    }

    enableSelect(select) {
      if (!select) {
        return;
      }

      select.disabled = false;

      const group =
        select.closest(".form-group");

      if (group) {
        group.classList.remove("disabled");
      }
    }

    resetSelect(select, disable = true) {
      if (!select) {
        return;
      }

      this.selectRemoveOptions(select);

      if (disable) {
        this.disableSelect(select);
      } else {
        this.enableSelect(select);
      }
    }

    hasValue(select) {
      return !!select && !!select.value;
    }

    parseHTML(text) {
      return new DOMParser().parseFromString(
        text,
        "text/html"
      );
    }

    /* ============================================================
       REMOVE "(1)", "(2)", "(25)" ETC.
    ============================================================ */

    cleanOptionText(text) {
      if (!text) {
        return "";
      }

      /*
       * Examples:
       *
       * Audi (1)        -> Audi
       * Audi (10)       -> Audi
       * Audi (1,250)    -> Audi
       * R8 V10 (5)      -> R8 V10
       *
       * Only removes a number in brackets from
       * the END of the label.
       */
      return text
        .replace(/\s*\(\s*[\d,]+\s*\)\s*$/g, "")
        .trim();
    }

    cleanSelectOptionLabels(select) {
      if (!select) {
        return;
      }

      select
        .querySelectorAll("option:not(.default)")
        .forEach((option) => {
          option.textContent =
            this.cleanOptionText(
              option.textContent
            );
        });
    }

    /* ============================================================
       GET OPTIONS FROM SHOPIFY FILTER PAGE
    ============================================================ */

    getAvailableOptions(resultsMarkup, select) {
      if (!resultsMarkup || !select) {
        return [];
      }

      const label = select.dataset.label;

      if (!label) {
        return [];
      }

      let escapedLabel = label;

      /*
       * Escape filter title so characters like quotes
       * do not break querySelector.
       */
      if (
        window.CSS &&
        typeof window.CSS.escape === "function"
      ) {
        escapedLabel =
          CSS.escape(label);
      } else {
        escapedLabel =
          label.replace(/"/g, '\\"');
      }

      const options = Array.from(
        resultsMarkup.querySelectorAll(
          `select[data-label="${escapedLabel}"] option`
        )
      );

      return options.filter((option) => {
        /*
         * Do not copy fetched placeholder.
         */
        if (
          option.classList.contains("default")
        ) {
          return false;
        }

        /*
         * Ignore options with zero products.
         *
         * Keep data-count internally.
         * We only remove count from visible text.
         */
        if (
          option.dataset.count !== undefined &&
          option.dataset.count !== ""
        ) {
          return Number(option.dataset.count) > 0;
        }

        return true;
      });
    }

    appendAvailableOptions(select, options) {
      if (!select || !options.length) {
        return;
      }

      const clonedOptions =
        options.map((option) => {
          const clone =
            option.cloneNode(true);

          /*
           * REMOVE VISIBLE PRODUCT COUNT
           *
           * Audi (1) -> Audi
           */
          clone.textContent =
            this.cleanOptionText(
              clone.textContent
            );

          return clone;
        });

      select.append(...clonedOptions);

      const defaultOption =
        select.querySelector("option.default");

      if (defaultOption) {
        defaultOption.selected = true;
      }

      select.value = "";
    }

    abortPreviousRequest() {
      if (this.requestController) {
        this.requestController.abort();
      }

      this.requestController =
        new AbortController();

      return this.requestController;
    }

    /* ============================================================
       ============================================================
       HORIZONTAL FILTER
       ============================================================
       ============================================================ */

    initHorizontalFilter() {
      /*
       * NEW BEHAVIOR
       *
       * Make     = ENABLED
       * Model    = ENABLED
       * Category = ENABLED
       *
       * No dependency between them.
       */

      this.selects.forEach((select) => {
        this.enableSelect(select);
      });

      if (this.select) {
        this.enableSelect(this.select);

        /*
         * Make sure configured collection names don't
         * accidentally contain counts either.
         */
        this.cleanSelectOptionLabels(
          this.select
        );
      }

      /*
       * Search disabled until at least ONE value
       * has been selected.
       */
      if (this.button) {
        this.button.disabled = true;
      }

      /*
       * Make and Model options need to be retrieved
       * from Shopify.
       *
       * We populate ALL product filter dropdowns
       * independently from /collections/all.
       */
      this.populateAllHorizontalFilters();
    }

    /* ============================================================
       LOAD MAKE + MODEL INDEPENDENTLY
    ============================================================ */

    async populateAllHorizontalFilters() {
      /*
       * Nothing to fetch when no product filters exist.
       */
      if (!this.selects.length) {
        this.updateHorizontalButtonState();

        this.setFormUrl(
          this.constructURL(),
          true
        );

        return;
      }

      /*
       * IMPORTANT:
       *
       * Always get available Make / Model values
       * from the entire source collection.
       *
       * This is why Model no longer depends upon Make.
       */
      const collectionUrl =
        this.input?.value ||
        window.routes.all_products_url;

      const url = new URL(
        collectionUrl,
        window.requestShopUrl
      );

      url.searchParams.set(
        "view",
        "do_not_use"
      );

      this.toggleLoading(true);

      try {
        const controller =
          this.abortPreviousRequest();

        const response =
          await fetch(
            url.toString(),
            {
              signal:
                controller.signal,
            }
          );

        if (!response.ok) {
          throw new Error(
            `Filter request failed: ${response.status}`
          );
        }

        const text =
          await response.text();

        const resultsMarkup =
          this.parseHTML(text);

        /*
         * Populate EVERY dropdown separately.
         *
         * Make doesn't wait for anything.
         * Model doesn't wait for anything.
         */
        this.selects.forEach((select) => {
          const options =
            this.getAvailableOptions(
              resultsMarkup,
              select
            );

          this.selectRemoveOptions(
            select
          );

          this.appendAvailableOptions(
            select,
            options
          );

          /*
           * ALWAYS ENABLE.
           */
          this.enableSelect(select);

          /*
           * Extra cleanup as a safeguard.
           */
          this.cleanSelectOptionLabels(
            select
          );
        });

        /*
         * Category always enabled.
         */
        if (this.select) {
          this.enableSelect(
            this.select
          );
        }

        this.setFormUrl(
          this.constructURL(),
          true
        );

        this.updateHorizontalButtonState();
      } catch (error) {
        if (
          error.name === "AbortError"
        ) {
          return;
        }

        console.error(
          "Unable to populate horizontal product filters:",
          error
        );

        /*
         * Even if AJAX failed we don't want
         * the UI locked.
         */
        this.selects.forEach(
          (select) => {
            this.enableSelect(
              select
            );
          }
        );

        if (this.select) {
          this.enableSelect(
            this.select
          );
        }
      } finally {
        this.toggleLoading(false);

        this.updateHorizontalButtonState();
      }
    }

    /* ============================================================
       MAKE / MODEL CHANGED
    ============================================================ */

    onHorizontalFilterChange() {
      /*
       * NEW BEHAVIOR:
       *
       * DO NOT:
       *
       * - reset another select
       * - disable another select
       * - fetch the next select
       * - require previous select
       *
       * Simply update search URL.
       */

      this.setFormUrl(
        this.constructURL(),
        true
      );

      this.updateHorizontalButtonState();
    }

    /* ============================================================
       CATEGORY CHANGED
    ============================================================ */

    onHorizontalCollectionChange() {
      /*
       * Category does NOT reset Make/Model.
       */

      this.setFormUrl(
        this.constructURL(),
        true
      );

      this.updateHorizontalButtonState();
    }

    /* ============================================================
       SEARCH BUTTON
    ============================================================ */

    updateHorizontalButtonState() {
      if (!this.button) {
        return;
      }

      /*
       * Is any product filter selected?
       *
       * Example:
       *
       * Make = Audi
       *
       * is enough.
       */
      const anyFilterSelected =
        this.selects.some(
          (select) =>
            this.hasValue(select)
        );

      /*
       * Is Category selected?
       */
      const categorySelected =
        this.select
          ? this.hasValue(this.select)
          : false;

      /*
       * User only needs ONE selection.
       */
      const canSearch =
        anyFilterSelected ||
        categorySelected;

      this.button.disabled =
        !canSearch;

      if (canSearch) {
        this.setFormUrl(
          this.constructURL(),
          true
        );
      }
    }

    /* ============================================================
       ============================================================
       ORIGINAL VERTICAL FILTER
       ============================================================
       ============================================================ */

    initDefaultFilter() {
      /*
       * Keep old vertical behavior.
       */
      if (!this.select) {
        this.populateCollectionFilters(
          true
        );
      } else {
        this.selects.forEach(
          (select) => {
            this.disableSelect(
              select
            );
          }
        );

        if (this.button) {
          this.button.disabled = true;
        }
      }
    }

    /* ============================================================
       VERTICAL FILTER CHANGE
    ============================================================ */

    onFilterChange(event) {
      const selectedIndex =
        Number(
          event.target.dataset.index
        );

      /*
       * Reset filters AFTER current filter.
       */
      this.selects.forEach(
        (select) => {
          const index =
            Number(
              select.dataset.index
            );

          if (
            index <= selectedIndex
          ) {
            return;
          }

          this.selectRemoveOptions(
            select
          );

          this.disableSelect(
            select
          );
        }
      );

      this.setFormUrl(
        this.constructURL()
      );

      const url =
        new URL(
          this.constructURL()
        );

      url.searchParams.set(
        "view",
        "do_not_use"
      );

      this.toggleLoading(true);

      const controller =
        this.abortPreviousRequest();

      fetch(
        url.toString(),
        {
          signal:
            controller.signal,
        }
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              response.status
            );
          }

          return response.text();
        })
        .then((text) => {
          const resultsMarkup =
            this.parseHTML(text);

          this.selects.forEach(
            (select) => {
              const index =
                Number(
                  select.dataset.index
                );

              if (
                index <=
                selectedIndex
              ) {
                return;
              }

              const options =
                this.getAvailableOptions(
                  resultsMarkup,
                  select
                );

              this.selectRemoveOptions(
                select
              );

              this.appendAvailableOptions(
                select,
                options
              );

              /*
               * Original vertical behavior:
               * enable only next dropdown.
               */
              if (
                index ===
                selectedIndex + 1
              ) {
                this.enableSelect(
                  select
                );
              }

              this.cleanSelectOptionLabels(
                select
              );
            }
          );
        })
        .catch((error) => {
          if (
            error.name ===
            "AbortError"
          ) {
            return;
          }

          console.error(
            "Vertical filter request failed:",
            error
          );
        })
        .finally(() => {
          this.toggleLoading(false);
        });
    }

    /* ============================================================
       POPULATE VERTICAL COLLECTION FILTERS
    ============================================================ */

    populateCollectionFilters(
      background = false
    ) {
      this.setFormUrl(
        this.constructURL(),
        background
      );

      const url =
        new URL(
          this.constructURL()
        );

      url.searchParams.set(
        "view",
        "do_not_use"
      );

      if (!background) {
        this.toggleLoading(true);
      }

      const controller =
        this.abortPreviousRequest();

      fetch(
        url.toString(),
        {
          signal:
            controller.signal,
        }
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              response.status
            );
          }

          return response.text();
        })
        .then((text) => {
          const resultsMarkup =
            this.parseHTML(text);

          this.selects.forEach(
            (select) => {
              const options =
                this.getAvailableOptions(
                  resultsMarkup,
                  select
                );

              this.selectRemoveOptions(
                select
              );

              this.appendAvailableOptions(
                select,
                options
              );

              /*
               * Vertical section:
               * only first filter unlocked.
               */
              if (
                Number(
                  select.dataset.index
                ) === 1
              ) {
                this.enableSelect(
                  select
                );
              } else {
                this.disableSelect(
                  select
                );
              }

              this.cleanSelectOptionLabels(
                select
              );
            }
          );
        })
        .catch((error) => {
          if (
            error.name ===
            "AbortError"
          ) {
            return;
          }

          console.error(
            "Unable to populate collection filters:",
            error
          );
        })
        .finally(() => {
          if (!background) {
            this.toggleLoading(false);
          }
        });
    }

    /* ============================================================
       VERTICAL COLLECTION CHANGED
    ============================================================ */

    onCollectionChange() {
      this.selects.forEach(
        (select) => {
          this.selectRemoveOptions(
            select
          );

          this.disableSelect(
            select
          );
        }
      );

      if (this.button) {
        this.button.disabled = true;
      }

      if (
        this.select &&
        !this.select.value
      ) {
        return;
      }

      this.populateCollectionFilters();
    }

    /* ============================================================
       LOADING
    ============================================================ */

    toggleLoading(forceState = null) {
      let loading;

      if (forceState === true) {
        this.classList.add(
          "is-loading"
        );

        loading = true;
      } else if (
        forceState === false
      ) {
        this.classList.remove(
          "is-loading"
        );

        loading = false;
      } else {
        loading =
          this.classList.toggle(
            "is-loading"
          );
      }

      if (!this.button) {
        return;
      }

      if (loading) {
        this.button.classList.add(
          "loading"
        );
      } else {
        this.button.classList.remove(
          "loading"
        );
      }
    }
  }

  customElements.define(
    "collections-filtering",
    CollectionsFiltering
  );
}